import { Component } from '@theme/component';

/**
 * Roberts Jewelers — click-to-play gallery video.
 *
 * The video starts PAUSED (poster visible, no autoplay). The corner button
 * toggles play/pause. The play and pause icons swap based on the `data-playing`
 * attribute on the host, which is driven by the video's own play/pause/ended
 * events — so the icon stays correct even when playback ends on its own.
 *
 * Markup:
 *   <roberts-video class="rj-gallery__cell-media rj-video">
 *     <video ref="video" …></video>
 *     <button class="rj-video__toggle" ref="toggleButton" on:click="/toggle">
 *       <span class="rj-video__icon rj-video__icon--play">…</span>
 *       <span class="rj-video__icon rj-video__icon--pause">…</span>
 *     </button>
 *   </roberts-video>
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video
 * @property {HTMLButtonElement} [toggleButton]
 *
 * @extends {Component<Refs>}
 */
class RobertsVideo extends Component {
  connectedCallback() {
    super.connectedCallback();
    const { video } = this.refs;
    if (video instanceof HTMLVideoElement) {
      video.addEventListener('play', this.#sync);
      video.addEventListener('pause', this.#sync);
      video.addEventListener('ended', this.#sync);
      this.#sync();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { video } = this.refs;
    if (video instanceof HTMLVideoElement) {
      video.removeEventListener('play', this.#sync);
      video.removeEventListener('pause', this.#sync);
      video.removeEventListener('ended', this.#sync);
    }
  }

  /** Play if paused, pause if playing. Wired via `on:click="/toggle"`. */
  toggle() {
    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.paused) {
      // User-initiated, so playback is allowed (with audio if present).
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  /** Reflect the real playback state onto the host + button. */
  #sync = () => {
    const { video, toggleButton } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;
    const playing = !video.paused && !video.ended;
    this.toggleAttribute('data-playing', playing);
    if (toggleButton instanceof HTMLElement) {
      toggleButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      toggleButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
    }
  };
}

if (!customElements.get('roberts-video')) {
  customElements.define('roberts-video', RobertsVideo);
}
